import React from 'react';
import PropTypes from 'prop-types';
import { borders, surfaces, textColors, typography } from '../../tokens';

/**
 * Card Component
 * 
 * A flexible card component for displaying content with consistent styling.
 * Supports variants, padding options, hover states, and composition via subcomponents.
 * 
 * @example
 * // Basic card
 * <Card>
 *   <p>Card content</p>
 * </Card>
 * 
 * @example
 * // Card with header and footer
 * <Card variant="elevated" hoverable>
 *   <CardHeader title="Card Title" subtitle="Subtitle text" />
 *   <CardBody>
 *     <p>Main content goes here</p>
 *   </CardBody>
 *   <CardFooter>
 *     <button>Action</button>
 *   </CardFooter>
 * </Card>
 * 
 * @example
 * // Clickable card
 * <Card hoverable onClick={() => console.log('Card clicked')}>
 *   <CardBody>Clickable content</CardBody>
 * </Card>
 */
const Card = ({
  children,
  variant = 'default',
  padding = 'md',
  hoverable = false,
  selected = false,
  onClick,
  className = '',
  as: Component = 'div',
  ...props
}) => {
  // Base classes
  const baseClasses = `rounded-2xl transition-all duration-200`;
  
  // Variant classes
  const variantClasses = {
    default: `${surfaces.primary} border ${borders.default}`,
    bordered: `${surfaces.primary} border-2 ${borders.strong}`,
    elevated: `${surfaces.primary} shadow-md border ${borders.default}`,
    flat: `${surfaces.primary} border-0`,
    glass: `${surfaces.glassCard} border ${borders.glass} shadow-sm`,
  };
  
  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };
  
  // Hover classes
  const hoverClasses = hoverable 
    ? `hover:shadow-lg ${surfaces.hover} hover:border-gray-300 dark:hover:border-dark-600 cursor-pointer`
    : '';
  
  // Selected classes
  const selectedClasses = selected
    ? 'border-blue-600 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-900/50 shadow-md'
    : '';
  
  // Combine all classes
  const combinedClasses = [
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    hoverClasses,
    selectedClasses,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <Component
      className={combinedClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyPress={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      {...props}
    >
      {children}
    </Component>
  );
};

Card.propTypes = {
  /** Card content */
  children: PropTypes.node.isRequired,
  /** Visual style variant */
  variant: PropTypes.oneOf(['default', 'bordered', 'elevated', 'flat', 'glass']),
  /** Internal padding size */
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  /** Whether card should have hover effects */
  hoverable: PropTypes.bool,
  /** Whether card is selected/active */
  selected: PropTypes.bool,
  /** Click handler for interactive cards */
  onClick: PropTypes.func,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** HTML element or React component to render as */
  as: PropTypes.elementType,
};

/**
 * CardHeader Component
 * 
 * Header section for cards with optional title, subtitle, and actions.
 * 
 * @example
 * <CardHeader 
 *   title="Card Title" 
 *   subtitle="Optional subtitle"
 *   actions={<button>Action</button>}
 * />
 */
export const CardHeader = ({
  title,
  subtitle,
  actions,
  className = '',
  ...props
}) => {
  return (
    <div
      className={`flex items-start justify-between ${className}`}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={`${typography.h5} ${textColors.primary} truncate`}>
            {title}
          </h3>
        )}
        {subtitle && (
          <p className={`mt-1 ${typography.bodySmall} ${textColors.muted}`}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex-shrink-0 ml-4">
          {actions}
        </div>
      )}
    </div>
  );
};

CardHeader.propTypes = {
  /** Card title */
  title: PropTypes.node,
  /** Card subtitle */
  subtitle: PropTypes.node,
  /** Action buttons or elements */
  actions: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

/**
 * CardBody Component
 * 
 * Main content area of the card.
 * 
 * @example
 * <CardBody>
 *   <p>Main card content</p>
 * </CardBody>
 */
export const CardBody = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={`${className}`} {...props}>
      {children}
    </div>
  );
};

CardBody.propTypes = {
  /** Body content */
  children: PropTypes.node.isRequired,
  /** Additional CSS classes */
  className: PropTypes.string,
};

/**
 * CardFooter Component
 * 
 * Footer section for cards, typically containing actions or metadata.
 * 
 * @example
 * <CardFooter>
 *   <button>Cancel</button>
 *   <button>Save</button>
 * </CardFooter>
 */
export const CardFooter = ({
  children,
  align = 'right',
  className = '',
  ...props
}) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };
  
  return (
    <div
      className={`flex items-center ${alignClasses[align]} space-x-2 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

CardFooter.propTypes = {
  /** Footer content */
  children: PropTypes.node.isRequired,
  /** Horizontal alignment of content */
  align: PropTypes.oneOf(['left', 'center', 'right', 'between']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Card;
